import { getMessageEntityType } from './client-request-action-utils'

/**
 * How a grid row maps onto its message conversation.
 *
 * `entityTypeId` is always explicit here, including for WorkOrder/Assignment rows. The grid cell
 * historically omitted it and leaned on `MessageTrigger`'s `entityTypeId = "Assignment"` default —
 * fine for that one call site, and a trap for any other caller, which is exactly what the bulk
 * actions are.
 */
export interface MessageConversationKey {
  entityId: string
  entityTypeId: string
}

/**
 * The conversation key for one row, or `null` when the row has no thread to address.
 *
 * **Single source of truth — do not re-derive this inline.** It was previously an anonymous
 * function inside the grid's message column, which meant the bulk actions had no way to reach it
 * and would have had to reimplement the branching. Getting it wrong does not fail loudly: it
 * addresses a conversation that does not exist, so messages appear to vanish.
 */
export function resolveMessageConversationKey(
  contextEntityType: string,
  item: Record<string, any>
): MessageConversationKey | null {
  // A row that already carries its resolved key — the messages inbox, where each row came straight from
  // my-states and mixes entity types the single `contextEntityType` param can't describe — is trusted
  // directly. Grid rows carry neither field, so this never fires for them.
  if (item.entityTypeId && item.entityId) {
    return { entityId: String(item.entityId), entityTypeId: String(item.entityTypeId) }
  }

  if (contextEntityType === 'ClientRequest') {
    // Onboarding requests originate in the client portal, where the conversation is keyed on the
    // portal request id — carried on preVerificationRequestId, because the unified-index projection
    // moved it off requestId (which is now the FBO doc id). Every other request type still uses
    // requestId.
    const isOnboarding = String(item.requestType ?? '').toLowerCase() === 'onboarding'
    const conversationId = isOnboarding ? item.preVerificationRequestId : item.requestId

    return conversationId
      ? {
          entityId: String(conversationId),
          entityTypeId: getMessageEntityType(item.requestType, item.version),
        }
      : null
  }

  if (contextEntityType === 'AtsPlacementRequest' || contextEntityType === 'VmsRequest') {
    // ATS / VMS threads key on the request's own Cosmos doc id, carried on requestId — NOT item.id,
    // which is the SHA-derived unified-index row key and useless as a conversation key. Stable from
    // creation and independent of any later FBO linking. WO-stage retention resolves these threads
    // by doc id, so that join breaks if the key is anything else.
    return item.requestId
      ? { entityId: String(item.requestId), entityTypeId: contextEntityType }
      : null
  }

  // WorkOrder / Assignment rows: both stages share one conversation, anchored on the assignment.
  return item.fboAssignmentId
    ? { entityId: String(item.fboAssignmentId), entityTypeId: 'Assignment' }
    : null
}
